import { useAsyncAction } from "../hooks/useAsyncAction";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@components/ui/button";
import { Card, CardContent } from "@components/ui/card";
import { Skeleton } from "@components/ui/skeleton";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import {
  approveEntity,
  assignEntity,
  bulkReviewEntities,
  getReviewQueue,
  overrideAssignment,
  updateEntity,
  type EntitySummary,
} from "../lib/auth";
import type { AssigneeType } from "@io-spoke/contracts/assignments";
import { PageHeader } from "@components/layout/PageHeader";
import { ConfirmDialog } from "@components/ui/confirm-dialog";
import { PageShell } from "@components/layout/PageShell";
import { ReviewQueueBulkActions } from "@components/review-queue/ReviewQueueBulkActions";
import { ReviewQueueCard } from "@components/review-queue/ReviewQueueCard";
import { ReviewQueueFilters } from "@components/review-queue/ReviewQueueFilters";
import { ReviewQueueViewToggle } from "@components/review-queue/ReviewQueueViewToggle";
import { useUserPreferences } from "@components/layout/UserPreferencesProvider";
import { formatTitleCase } from "../lib/formatting";
import { toast } from "sonner";

const bulkResultCopy: Record<"approve" | "reject", { verb: string; destination: string | null }> = {
  approve: { verb: "approved", destination: "/lens/execution" },
  reject: { verb: "rejected", destination: null },
};

type ReviewQueuePageProps = {
  navigate: (route: string) => void;
};

export function ReviewQueuePage({ navigate }: ReviewQueuePageProps) {
  const { preferences, savePreferences } = useUserPreferences();
  const [reviewItems, setReviewItems] = useState<EntitySummary[]>([]);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(15);
  const [slaFilter, setSlaFilter] = useState<"all" | "breached">("all");
  const [queueFilter, setQueueFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkResults, setBulkResults] = useState<Record<string, string>>({});
  const [bulkSummary, setBulkSummary] = useState<{
    action: "approve" | "reject";
    successCount: number;
    failureCount: number;
    failureIds: string[];
  } | null>(null);
  const bulkAction = useAsyncAction();
  const [pendingBulkAction, setPendingBulkAction] = useState<"approve" | "reject" | null>(null);
  const [compactView, setCompactView] = useState(
    preferences.density === "compact"
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [saveStateById, setSaveStateById] = useState<
    Record<string, { status: "saving" | "saved" | "error"; message?: string }>
  >({});
  const [lastActionById, setLastActionById] = useState<
    Record<
      string,
      | { type: "update"; updates: Parameters<typeof updateEntity>[1] }
      | { type: "assign"; assigneeType: AssigneeType; assigneeId: string }
    >
  >({});
  const queueOptions = useMemo(() => {
    const teamIds = Array.from(
      new Set(
        reviewItems
          .filter((item) => item.assignment?.assigneeType === "team")
          .map((item) => item.assignment?.assigneeId)
          .filter((id): id is string => Boolean(id))
      )
    ).sort();

    return [
      { value: "all", label: "All queues" },
      { value: "fallback", label: "Fallback queue" },
      ...teamIds.map((teamId) => ({
        value: `team:${teamId}`,
        label: `${formatTitleCase(teamId)} queue`,
      })),
    ];
  }, [reviewItems]);
  const filteredReviewItems = useMemo(() => {
    if (queueFilter === "all") {
      return reviewItems;
    }
    if (queueFilter === "fallback") {
      return reviewItems.filter((item) => item.assignment?.isFallback);
    }
    if (queueFilter.startsWith("team:")) {
      const teamId = queueFilter.slice("team:".length);
      return reviewItems.filter(
        (item) =>
          item.assignment?.assigneeType === "team" &&
          item.assignment?.assigneeId === teamId
      );
    }
    return reviewItems;
  }, [queueFilter, reviewItems]);

  useEffect(() => {
    setVisibleCount(15);
  }, [queueFilter, slaFilter]);

  const hasReviewItems = reviewItems.length > 0;
  const hasFilteredItems = filteredReviewItems.length > 0;
  const activeQueueLabel =
    queueOptions.find((option) => option.value === queueFilter)?.label ??
    "selected queue";

  const handleDensityChange = async (value: boolean) => {
    setCompactView(value);
    try {
      await savePreferences({ density: value ? "compact" : "comfortable" });
    } catch {
      // Error handling is managed in the preferences provider.
    }
  };

  useEffect(() => {
    setCompactView(preferences.density === "compact");
  }, [preferences.density]);

  useEffect(() => {
    let isActive = true;
    setIsReviewLoading(true);
    setReviewError(null);
    getReviewQueue({ sla: slaFilter === "breached" ? "breached" : undefined })
      .then((items) => {
        if (isActive) {
          setReviewItems(items);
          setVisibleCount(15);
        }
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load review queue.";
        setReviewError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsReviewLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [slaFilter]);

  useEffect(() => {
    setSelectedIds((current) => {
      if (current.size === 0) {
        return current;
      }
      const next = new Set<string>();
      reviewItems.forEach((item) => {
        if (current.has(item.id)) {
          next.add(item.id);
        }
      });
      return next;
    });
    setBulkResults((current) => {
      const keys = Object.keys(current);
      if (keys.length === 0) {
        return current;
      }
      const next: Record<string, string> = {};
      reviewItems.forEach((item) => {
        if (current[item.id]) {
          next[item.id] = current[item.id];
        }
      });
      return next;
    });
    setExpandedIds((current) => {
      if (current.size === 0) {
        return current;
      }
      const next = new Set<string>();
      reviewItems.forEach((item) => {
        if (current.has(item.id)) {
          next.add(item.id);
        }
      });
      return next;
    });
  }, [reviewItems]);

  const handleReviewUpdate = async (
    entityId: string,
    updates: {
      title?: string;
      description?: string;
      systems?: string[];
      customerImpact?: string;
      priority?: string;
    }
  ) => {
    setReviewError(null);
    setLastActionById((current) => ({
      ...current,
      [entityId]: { type: "update", updates },
    }));
    setSaveStateById((current) => ({
      ...current,
      [entityId]: { status: "saving" },
    }));
    try {
      await updateEntity(entityId, updates);
      setSaveStateById((current) => ({
        ...current,
        [entityId]: { status: "saved", message: "Changes saved." },
      }));
      setTimeout(() => {
        setSaveStateById((current) => {
          if (current[entityId]?.status !== "saved") {
            return current;
          }
          const { [entityId]: _, ...rest } = current;
          return rest;
        });
      }, 2000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to update work item.";
      setReviewError(message);
      setSaveStateById((current) => ({
        ...current,
        [entityId]: { status: "error", message },
      }));
    }
  };

  const handleAssign = async (
    entityId: string,
    assigneeType: AssigneeType,
    assigneeId: string
  ) => {
    setReviewError(null);
    setLastActionById((current) => ({
      ...current,
      [entityId]: { type: "assign", assigneeType, assigneeId },
    }));
    setSaveStateById((current) => ({
      ...current,
      [entityId]: { status: "saving" },
    }));
    try {
      await assignEntity(entityId, { assigneeType, assigneeId });
      setSaveStateById((current) => ({
        ...current,
        [entityId]: { status: "saved", message: "Assignment saved." },
      }));
      setTimeout(() => {
        setSaveStateById((current) => {
          if (current[entityId]?.status !== "saved") {
            return current;
          }
          const { [entityId]: _, ...rest } = current;
          return rest;
        });
      }, 2000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to assign work item.";
      setReviewError(message);
      setSaveStateById((current) => ({
        ...current,
        [entityId]: { status: "error", message },
      }));
    }
  };

  const handleRetry = async (entityId: string) => {
    const lastAction = lastActionById[entityId];
    if (!lastAction) {
      return;
    }
    if (lastAction.type === "update") {
      await handleReviewUpdate(entityId, lastAction.updates);
      return;
    }
    await handleAssign(entityId, lastAction.assigneeType, lastAction.assigneeId);
  };

  const handleBulkAction = (action: "approve" | "reject") => {
    if (selectedIds.size === 0) {
      setReviewError("Select at least one work item to apply bulk actions.");
      return;
    }
    setPendingBulkAction(action);
  };

  const runBulkAction = async (action: "approve" | "reject") => bulkAction.run(async () => {
    setReviewError(null);
    setBulkSummary(null);
    try {
      const results = await bulkReviewEntities(action, Array.from(selectedIds));
      const nextResults: Record<string, string> = {};
      const failureIds: string[] = [];
      const successIds = new Set<string>();
      results.forEach((result) => {
        if (result.status === "error") {
          nextResults[result.id] = result.error ?? "Unable to update work item.";
          failureIds.push(result.id);
          return;
        }
        successIds.add(result.id);
      });
      setBulkSummary({
        action,
        successCount: successIds.size,
        failureCount: failureIds.length,
        failureIds,
      });
      setBulkResults(nextResults);
      setReviewItems((current) =>
        current.filter((item) => !successIds.has(item.id))
      );
      setSelectedIds(new Set());
      if (successIds.size > 0) {
        const copy = bulkResultCopy[action];
        const destination = copy.destination;
        toast.success(`${successIds.size} work item${successIds.size === 1 ? "" : "s"} ${copy.verb}.`, {
          action: destination ? { label: "View approved work", onClick: () => navigate(destination) } : undefined,
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to run bulk actions.";
      setReviewError(message);
      throw err;
    }
  });

  const handleOverride = async (
    entityId: string,
    assigneeType: AssigneeType,
    assigneeId: string,
    rationale: string
  ) => {
    setReviewError(null);
    try {
      await overrideAssignment(entityId, {
        assigneeType,
        assigneeId,
        rationale,
      });
      const updated = await getReviewQueue();
      setReviewItems(updated);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to override assignment.";
      setReviewError(message);
    }
  };

  const handleApprove = async (entityId: string) => {
    setReviewError(null);
    try {
      const approved = await approveEntity(entityId);
      const updated = await getReviewQueue();
      setReviewItems(updated);
      toast.success("Work item approved.", {
        description: "It is ready for execution and integrations.",
        action: { label: "View item", onClick: () => navigate(`/entities/${approved.id}`) },
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to approve work item.";
      setReviewError(message);
    }
  };

  const handleJumpToFailures = () => {
    if (!bulkSummary || bulkSummary.failureCount === 0) {
      return;
    }
    const firstFailureId = bulkSummary.failureIds.find((id) =>
      reviewItems.some((item) => item.id === id)
    );
    if (!firstFailureId) {
      return;
    }
    const target = document.getElementById(`review-queue-item-${firstFailureId}`);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
    target?.focus?.();
  };

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/inbox")}>
              Inbox
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Review queue</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Proposed work"
        title="Review queue"
        description="Review AI-proposed work items before approval."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/inbox")}>
              Inbox
            </Button>
            <Button variant="outline" onClick={() => navigate("/sla-rules")}>
              SLA rules
            </Button>
          </>
        }
      />

      <section className="mt-8 space-y-4">
        {isReviewLoading ? (
          <div className="space-y-4">
            <Card className="border-border bg-card/60">
              <CardContent className="flex flex-wrap items-center gap-3 py-4">
                <Skeleton className="h-9 w-40" />
                <Skeleton className="h-9 w-32" />
                <Skeleton className="h-9 w-28" />
              </CardContent>
            </Card>
            {Array.from({ length: 3 }).map((_, index) => (
              <Card key={`review-skeleton-${index}`} className="border-border bg-card/60">
                <CardContent className="space-y-3 py-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-6 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                  <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-8 w-32" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : reviewError ? (
          <p className="text-sm text-red-400" role="alert">
            {reviewError}
          </p>
        ) : !hasReviewItems ? (
          <Empty className="border-border bg-card/60">
            <EmptyHeader>
              <EmptyTitle>No proposed work items</EmptyTitle>
              <EmptyDescription className="text-muted-foreground">
                Propose a work item from a conversation to populate this queue.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={() => navigate("/inbox")}>
                Go to inbox
              </Button>
            </EmptyContent>
          </Empty>
        ) : !hasFilteredItems ? (
          <Empty className="border-border bg-card/60">
            <EmptyHeader>
              <EmptyTitle>No items in this queue</EmptyTitle>
              <EmptyDescription className="text-muted-foreground">
                There are no proposed work items in {activeQueueLabel.toLowerCase()}.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setQueueFilter("all")}>
                Show all queues
              </Button>
              <Button variant="outline" onClick={() => navigate("/inbox")}>
                Go to inbox
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <>
            <ReviewQueueFilters
              slaFilter={slaFilter}
              queueFilter={queueFilter}
              queueOptions={queueOptions}
              onSlaChange={(value) => setSlaFilter(value)}
              onQueueChange={(value) => setQueueFilter(value)}
            />
            {bulkSummary ? (
                <Card
                  className={
                    bulkSummary.failureCount > 0
                      ? "border-amber-500/70 bg-amber-950/20"
                      : "border-emerald-700/50 bg-emerald-950/30"
                  }
                >
                <CardContent
                  className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm text-foreground"
                  role={bulkSummary.failureCount > 0 ? "alert" : "status"}
                  aria-live="polite"
                >
                  <div>
                    {bulkSummary.action === "approve" ? "Bulk approve" : "Bulk reject"}{" "}
                    complete: {bulkSummary.successCount} succeeded,{" "}
                    {bulkSummary.failureCount} failed.
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {bulkSummary.failureCount > 0 ? (
                      <Button variant="outline" onClick={handleJumpToFailures}>
                        Jump to failures
                      </Button>
                    ) : null}
                    <Button
                      variant="ghost"
                      onClick={() => setBulkSummary(null)}
                    >
                      Dismiss
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : null}
            <ReviewQueueBulkActions
              busy={bulkAction.pending}
              selectedCount={selectedIds.size}
              totalCount={filteredReviewItems.length}
              onApprove={() => handleBulkAction("approve")}
              onReject={() => handleBulkAction("reject")}
              onClear={() => setSelectedIds(new Set())}
            />
            <ConfirmDialog
              open={pendingBulkAction !== null}
              onOpenChange={(open) => {
                if (!open && !bulkAction.pending) setPendingBulkAction(null);
              }}
              title={
                pendingBulkAction === "approve"
                  ? "Approve the selected work items?"
                  : "Reject the selected work items?"
              }
              description={`${selectedIds.size} selected work item${selectedIds.size === 1 ? "" : "s"} will be updated.`}
              confirmLabel={pendingBulkAction === "approve" ? "Approve" : "Reject"}
              destructive={pendingBulkAction === "reject"}
              onConfirm={async () => {
                const action = pendingBulkAction;
                if (!action) return;
                await runBulkAction(action);
                setPendingBulkAction(null);
              }}
            />
            <ReviewQueueViewToggle
              compactView={compactView}
              onChange={handleDensityChange}
            />
            {filteredReviewItems.slice(0, visibleCount).map((item) => (
              <ReviewQueueCard
                key={item.id}
                cardId={`review-queue-item-${item.id}`}
                item={item}
                compactView={compactView}
                isExpanded={expandedIds.has(item.id)}
                onToggleExpand={() =>
                  setExpandedIds((current) => {
                    const next = new Set(current);
                    if (next.has(item.id)) {
                      next.delete(item.id);
                    } else {
                      next.add(item.id);
                    }
                    return next;
                  })
                }
                selected={selectedIds.has(item.id)}
                onSelectChange={(checked) =>
                  setSelectedIds((current) => {
                    const next = new Set(current);
                    if (checked) {
                      next.add(item.id);
                    } else {
                      next.delete(item.id);
                    }
                    return next;
                  })
                }
                saveState={saveStateById[item.id]}
                onRetry={() => handleRetry(item.id)}
                bulkError={bulkResults[item.id]}
                bulkHighlight={Boolean(bulkResults[item.id])}
                onUpdate={(updates) => handleReviewUpdate(item.id, updates)}
                onAssign={(assigneeType, assigneeId) =>
                  handleAssign(item.id, assigneeType, assigneeId)
                }
                onOverride={(assigneeType, assigneeId, rationale) =>
                  handleOverride(item.id, assigneeType, assigneeId, rationale)
                }
                onApprove={() => handleApprove(item.id)}
                onViewDetail={() => navigate(`/entities/${item.id}`)}
              />
            ))}
            {visibleCount < filteredReviewItems.length ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setVisibleCount((count) => count + 10)}
              >
                Load more
              </Button>
            ) : null}
          </>
        )}
      </section>
    </PageShell>
  );
}
